import React, { lazy, Suspense, useEffect, useState } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AchievementProvider } from "@/hooks/use-achievements";
import MainNavigation from "@/components/MainNavigation";
import TopBanner from "@/components/TopBanner";
import MigrationBanner from "@/components/MigrationBanner";
import Footer from "@/components/Footer";
import LoadingSpinner from "@/components/LoadingSpinner";
import SmartNotificationTrigger from "@/components/notifications/SmartNotificationTrigger";
import { analytics } from "@/services/AnalyticsService";
import { initServiceWorkerUpdateHandler, checkForWaitingServiceWorker } from "@/lib/serviceWorkerUpdate";

// Lazy load all non-critical components for better performance
const Home = lazy(() => import("@/pages/Home"));
const Register = lazy(() => import("@/pages/Register"));
const AchievementsPage = lazy(() => import("@/pages/AchievementsPage"));
const EnhancedAchievementsPage = lazy(() => import("@/pages/EnhancedAchievementsPage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const AgencyAssessmentPage = lazy(() => import("@/pages/AgencyAssessmentPage"));
const TechReadinessPage = lazy(() => import("@/pages/TechReadinessPage"));
const TechBaselineAssessmentPage = lazy(() => import("@/pages/TechBaselineAssessmentPage"));
const LearnerJourneyPage = lazy(() => import("@/pages/LearnerJourneyPage"));
const ResearchPage = lazy(() => import("@/pages/ResearchPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const AILearningInsightsPage = lazy(() => import("@/pages/AILearningInsightsPage"));
const MobilePWADemoPage = lazy(() => import("@/pages/MobilePWADemoPage"));
const SecurityDashboardPage = lazy(() => import("@/pages/SecurityDashboardPage"));
const SystemHealthPage = lazy(() => import("@/pages/SystemHealthPage"));
const AppOverviewPage = lazy(() => import("@/pages/AppOverviewPage"));
const SettingsPage = lazy(() => import("@/pages/SettingsPage"));
const CommunicationsPage = lazy(() => import("@/pages/CommunicationsPage"));
const NotificationsPage = lazy(() => import("@/pages/NotificationsPage"));
const Dashboard = lazy(() => import("@/pages/dashboard"));
const DataAdminPortalPage = lazy(() => import("@/pages/DataAdminPortalPage"));
const AdminDashboardPreview = lazy(() => import("@/pages/AdminDashboardPreview"));
const UnauthorizedPage = lazy(() => import("@/pages/UnauthorizedPage"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const OrganizationPortalPage = lazy(() => import("@/pages/OrganizationPortalPage"));
const DocumentsPage = lazy(() => import("@/pages/DocumentsPage"));
const MVPOnboarding = lazy(() => import("@/pages/MVPOnboarding"));
const MVPStatusPage = lazy(() => import("@/pages/MVPStatusPage"));
const Enterprise = lazy(() => import("@/pages/Enterprise"));
const RedHelicopterJourneyPage = lazy(() => import("@/pages/RedHelicopterJourneyPage"));
const PreviewPage = lazy(() => import("@/pages/PreviewPage"));
const LogoutPage = lazy(() => import("@/pages/LogoutPage"));
const SelfReportingPage = lazy(() => import("@/pages/SelfReportingPage"));
const RoadmapFutures = lazy(() => import("@/pages/RoadmapFutures"));

// Keep only critical authentication components loaded immediately
import Login from "@/pages/Login";

// Lazy load scenarios app
const MoneyScenariosApp = lazy(() => import("@/components/scenarios/MoneyScenariosApp"));
import NotFound from "@/pages/not-found";
import LoginRedirect from "@/components/LoginRedirect";
import AuthCallback from "@/pages/AuthCallback";

// Lazy load heavy journey components only when needed
const InteractiveHelicopter = lazy(() => import("@/pages/InteractiveHelicopter"));

import { useAuth } from "@/hooks/useAuth";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { JourneyProvider } from "@/hooks/useJourneyContext";
import { preloadCriticalAssets } from "@/lib/performance";
import PerformanceMonitor from "@/components/PerformanceMonitor";
import { supabase } from "@/lib/supabase";

// Mock user ID until we have auth in place
const TEMP_USER_ID = "1";

function Router() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { isAdminAuthenticated } = useAdminAuth();
  const [location, setLocation] = useLocation();
  const [hasProcessedLoginRedirect, setHasProcessedLoginRedirect] = useState(false);
  const queryClientHook = useQueryClient();

  // Domain detection for scenarios
  const hostname = window.location.hostname;
  const isScenariosDomain = hostname === 'scenarios.redheli.com';

  // Check if user is admin - support both beta users and Supabase admin auth
  const isAdmin = (user?.role === 'admin' || user?.email === 'admin@redheli.com') || isAdminAuthenticated;
  
  // Initialize analytics with user context
  useEffect(() => {
    if (user?.id) {
      analytics.initialize(user.id, user.organizationId);
    }
  }, [user]);
  
  // Track page views on route change
  useEffect(() => {
    analytics.trackPageView(location);
  }, [location]);

  // Handle login redirect immediately to prevent 404 flash
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    
    // Handle both regular login and SAML login redirects
    const isLoginRedirect = urlParams.get('login') === 'success' || hash === '#step3';
    const isSamlLogin = urlParams.get('saml_login') === 'success';
    const shouldRefreshAuth = urlParams.get('refresh_auth') === 'true';
    
    // Also check for login success flag which indicates we're in a post-login state
    const hasLoginSuccessFlag = sessionStorage.getItem('loginSuccess') === 'true';
    
    if ((isLoginRedirect || isSamlLogin || hasLoginSuccessFlag) && !hasProcessedLoginRedirect) {
      // Track login redirect processing
      analytics.trackAction('login_redirect_processed', 'auth', isSamlLogin ? 'saml' : 'regular');
      
      // Clean the URL immediately to prevent 404 flash
      if (isLoginRedirect || isSamlLogin) {
        window.history.replaceState({}, '', '/');
      }
      setHasProcessedLoginRedirect(true);
      
      // For SAML login, force refresh the auth cache
      if (isSamlLogin && shouldRefreshAuth) {
        // Track SAML authentication processed
        analytics.trackAction('saml_auth_processed', 'auth');
        queryClientHook.invalidateQueries({ queryKey: ["auth/user"] });
        queryClientHook.removeQueries({ queryKey: ["auth/user"] });
      }
      
      // Don't make routing decisions until auth is fully loaded
      if (!isLoading) {
        if (isAuthenticated && user) {
          // Track successful authentication
          analytics.trackAction('authentication_success', 'auth', user.role);
          sessionStorage.setItem('loginSuccess', 'true');
          setLocation('/');
        } else if (!hasLoginSuccessFlag) {
          // Only redirect to login-redirect if we don't have the success flag
          // This prevents unnecessary redirects when auth is still loading
          setLocation('/login-redirect');
        }
      }
      return;
    }
  }, [setLocation, isAuthenticated, user, isLoading, hasProcessedLoginRedirect]);

  // Preload critical assets on component mount + progressive loading
  useEffect(() => {
    preloadCriticalAssets();
    // Import progressive loading after initial render
    import('./lib/performance').then(({ preloadAssetsOnInteraction }) => {
      preloadAssetsOnInteraction();
    });
  }, []);

  // Don't show loading state for initial render - let the journey component handle it
  // This prevents the flash of loading screen
  
  // If on scenarios domain, render scenarios app instead
  if (isScenariosDomain) {
    return (
      <Suspense fallback={<LoadingSpinner />}>
        <MoneyScenariosApp />
      </Suspense>
    );
  }
  
  // Show loading state during authentication check to prevent 404 flash
  if (isLoading && sessionStorage.getItem('loginSuccess') === 'true') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-white to-blue-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-rh-helicopter-red mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your journey...</p>
        </div>
      </div>
    );
  }
  
  return (
    <Suspense fallback={<div className="min-h-screen bg-black"></div>}>
      <Switch>
        {/* Main Red Helicopter Portal - MUST BE FIRST to prevent admin flash */}
        <Route path="/" component={RedHelicopterJourneyPage} />
        
        {/* Public routes */}
        <Route path="/login" component={Login} />
        <Route path="/register" component={Register} />
        <Route path="/logout" component={LogoutPage} />

        {/* Authentication callback - handles email confirmations and password resets */}
        <Route path="/auth/callback" component={AuthCallback} />

        {/* Login redirect route - prevents 404 flash */}
        <Route path="/login-redirect" component={LoginRedirect} />
        
        {/* Preview route for Enhanced Pre-Flight experience */}
        <Route path="/preview" component={PreviewPage} />
        <Route path="/identity-transformation" component={PreviewPage} />
        <Route path="/pre-flight" component={PreviewPage} />
        <Route path="/step1" component={PreviewPage} />
        
        {/* Pre-flight experience accessible to all users */}
        <Route path="/welcome" component={InteractiveHelicopter} />
        <Route path="/helicopter" component={InteractiveHelicopter} />
        <Route path="/interactive-helicopter" component={InteractiveHelicopter} />
        <Route path="/landing" component={InteractiveHelicopter} />
        <Route path="/experience" component={InteractiveHelicopter} />
        
        {isAuthenticated ? (
          <>
            {/* Authenticated users have access to full journey functionality */}
            <Route path="/logout">
              {() => {
                // Clear all data and redirect to logout endpoint
                sessionStorage.clear();
                localStorage.clear();
                // Clear query client if available
                try {
                  queryClientHook.clear();
                } catch (error) {
                  console.log('QueryClient not available for cleanup');
                }
                window.location.href = '/api/simple-logout';
                return null;
              }}
            </Route>
            <Route path="/home" component={Home} />
            <Route path="/overview" component={AppOverviewPage} />
            <Route path="/onboarding" component={MVPOnboarding} />
            <Route path="/journey" component={RedHelicopterJourneyPage} />
            <Route path="/helicopter-journey" component={RedHelicopterJourneyPage} />
            <Route path="/6-step-journey" component={RedHelicopterJourneyPage} />
            <Route path="/pre-flight" component={RedHelicopterJourneyPage} />
            <Route path="/step1" component={RedHelicopterJourneyPage} />
            <Route path="/identity-transformation" component={RedHelicopterJourneyPage} />
            <Route path="/step2" component={RedHelicopterJourneyPage} />
            <Route path="/step3" component={RedHelicopterJourneyPage} />
            <Route path="/step4" component={RedHelicopterJourneyPage} />
            <Route path="/step5" component={RedHelicopterJourneyPage} />
            <Route path="/step6" component={RedHelicopterJourneyPage} />
            <Route path="/preview" component={PreviewPage} />
            <Route path="/achievements" component={EnhancedAchievementsPage} />
            <Route path="/flight-community" component={EnhancedAchievementsPage} />
            <Route path="/ai-insights" component={AILearningInsightsPage} />
            
            {/* Money Scenarios routes - accessible to authenticated users */}
            <Route path="/scenarios" component={MoneyScenariosApp} />
            <Route path="/money-scenarios" component={MoneyScenariosApp} />
            <Route path="/money/scenarios" component={MoneyScenariosApp} />
            <Route path="/money/1" component={MoneyScenariosApp} />
            <Route path="/money/2" component={MoneyScenariosApp} />
            <Route path="/money/3" component={MoneyScenariosApp} />
            <Route path="/mobile-pwa" component={MobilePWADemoPage} />
            <Route path="/security" component={SecurityDashboardPage} />
            <Route path="/research" component={ResearchPage} />
            <Route path="/profile" component={ProfilePage} />
            <Route path="/tech-readiness" component={TechReadinessPage} />
            <Route path="/tech-baseline" component={TechBaselineAssessmentPage} />
            <Route path="/agency-assessment" component={AgencyAssessmentPage} />
            <Route path="/surveys/agency-assessment" component={AgencyAssessmentPage} />
            <Route path="/privacy" component={PrivacyPage} />
            <Route path="/terms" component={TermsPage} />
            <Route path="/settings" component={SettingsPage} />
            <Route path="/communications" component={CommunicationsPage} />
            <Route path="/notifications" component={NotificationsPage} />
            <Route path="/documents" component={DocumentsPage} />
            <Route path="/roadmap" component={RoadmapFutures} />
            
            {/* Admin Login Route - Always available */}
            <Route path="/admin/login" component={AdminLogin} />

            {/* Admin Routes - Protected by authentication and admin role */}
            {isAdmin ? (
              <>
                <Route path="/admin" component={DataAdminPortalPage} />
                <Route path="/admin/dashboard" component={Dashboard} />
                <Route path="/admin/preview" component={AdminDashboardPreview} />
                <Route path="/admin/database-config" component={DataAdminPortalPage} />
                <Route path="/admin/tenants" component={DataAdminPortalPage} />
                <Route path="/admin/azure" component={DataAdminPortalPage} />
                <Route path="/admin/provision" component={DataAdminPortalPage} />
                <Route path="/admin/analytics" component={DataAdminPortalPage} />
                <Route path="/admin/logs" component={DataAdminPortalPage} />
                <Route path="/admin/security" component={SecurityDashboardPage} />
                <Route path="/admin/config" component={DataAdminPortalPage} />
              </>
            ) : (
              <>
                {/* Non-admin users trying to access admin routes get redirected to login */}
                <Route path="/admin" component={AdminLogin} />
                <Route path="/admin/:rest*" component={AdminLogin} />
              </>
            )}
            <Route path="/unauthorized" component={UnauthorizedPage} />
            
            <Route path="/mvp-status" component={MVPStatusPage} />
            <Route path="/organization" component={OrganizationPortalPage} />
            <Route path="/health" component={SystemHealthPage} />
            <Route path="/enterprise" component={Enterprise} />
            <Route path="/self-reporting" component={SelfReportingPage} />
            <Route path="/balance-tracking" component={SelfReportingPage} />
            <Route component={NotFound} />
          </>
        ) : (
          <>
            {/* Unauthenticated users see streamlined Connect-Engage pre-flight experience */}
            <Route path="/" component={RedHelicopterJourneyPage} />
            <Route path="/welcome" component={RedHelicopterJourneyPage} />
            <Route path="/sign-in" component={Login} />
            <Route path="/login" component={Login} />
            <Route path="/logout">
              {() => {
                // Clear all data and redirect to home for logged out users
                sessionStorage.clear();
                localStorage.clear();
                window.location.href = '/';
                return null;
              }}
            </Route>
            {/* All other routes should also show the Red Helicopter portal for unauthenticated users */}
            <Route component={RedHelicopterJourneyPage} />
          </>
        )}
      </Switch>
    </Suspense>
  );
}

function AppLayoutInner({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { isAuthenticated, user } = useAuth();
  const [isPortalStep, setIsPortalStep] = useState(() => {
    // Check sessionStorage to see if we're likely in a portal step
    const forceStep1 = sessionStorage.getItem('forceStep1Portal') === 'true';
    return forceStep1;
  });
  
  // Check if we're in Step 1 (Portal experience)
  useEffect(() => {
    // Check if user is in portal phase by looking at DOM or other indicators
    const checkPortalStep = () => {
      // Look for portal-specific elements or CSS classes
      const portalElements = document.querySelector('.cosmic-bg, [id="entrance"], [id="pre-flight"]');
      const hasPortalElements = !!portalElements;
      // Also check sessionStorage for Step 1 flag
      const forceStep1 = sessionStorage.getItem('forceStep1Portal') === 'true';
      setIsPortalStep(hasPortalElements || forceStep1);
    };
    
    // Check immediately and set up an observer
    checkPortalStep();
    
    // Set up a MutationObserver to watch for DOM changes
    const observer = new MutationObserver(checkPortalStep);
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => observer.disconnect();
  }, [location]);
  
  // For authenticated users showing Step 3+, always show navigation
  // This prevents the white flash by immediately showing the proper layout
  if (isAuthenticated && !isPortalStep) {
    return (
      <div className="min-h-screen flex flex-col">
        <TopBanner />
        <MigrationBanner />
        <MainNavigation />
        <main className="flex-grow pt-20 transition-all duration-300" style={{ paddingLeft: 'var(--sidebar-width, 0rem)' }}>
          {children}
        </main>
        <div className="transition-all duration-300" style={{ paddingLeft: 'var(--sidebar-width, 0rem)' }}>
          <Footer />
        </div>
        {/* Smart notification system - invisible but active */}
        <SmartNotificationTrigger />
      </div>
    );
  }
  
  // For Step 1 (Portal), render without header/footer
  if (isPortalStep) {
    return (
      <div className="min-h-screen">
        <main className="min-h-screen">
          {children}
        </main>
        {/* Smart notification system - invisible but active */}
        <SmartNotificationTrigger />
      </div>
    );
  }
  
  // Default layout for other cases
  return (
    <div className="min-h-screen flex flex-col">
      <TopBanner />
      <MigrationBanner />
      <MainNavigation />
      <main className="flex-grow pt-20 transition-all duration-300" style={{ paddingLeft: 'var(--sidebar-width, 0rem)' }}>
        {children}
      </main>
      <div className="transition-all duration-300" style={{ paddingLeft: 'var(--sidebar-width, 0rem)' }}>
        <Footer />
      </div>
      {/* Smart notification system - invisible but active */}
      <SmartNotificationTrigger />
    </div>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <AppLayoutInner>
      {children}
    </AppLayoutInner>
  );
}

function App() {
  // Initialize service worker update handler for automatic cache refresh
  useEffect(() => {
    // Initialize handlers for automatic cache updates
    initServiceWorkerUpdateHandler();
    checkForWaitingServiceWorker();

    // Also register service worker if not already registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js').then(registration => {
        console.log('[ServiceWorker] Registration successful:', registration.scope);
      }).catch(err => {
        console.error('[ServiceWorker] Registration failed:', err);
      });
    }
  }, []);

  // Supabase auth state listener for session management
  useEffect(() => {
    console.log('[SUPABASE-AUTH] Setting up auth state listener');

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[SUPABASE-AUTH] Auth event:', event);

      if (event === 'SIGNED_IN') {
        console.log('[SUPABASE-AUTH] User signed in');
        // Invalidate auth cache to trigger refetch
        queryClient.invalidateQueries({ queryKey: ['auth/user'] });
      } else if (event === 'SIGNED_OUT') {
        console.log('[SUPABASE-AUTH] User signed out');
        // Clear auth cache
        queryClient.setQueryData(['auth/user'], null);
      } else if (event === 'TOKEN_REFRESHED') {
        console.log('[SUPABASE-AUTH] Token refreshed successfully');
      } else if (event === 'USER_UPDATED') {
        console.log('[SUPABASE-AUTH] User data updated');
        queryClient.invalidateQueries({ queryKey: ['auth/user'] });
      }
    });

    // Cleanup subscription on unmount
    return () => {
      console.log('[SUPABASE-AUTH] Cleaning up auth state listener');
      subscription.unsubscribe();
    };
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AchievementProvider userId={TEMP_USER_ID}>
          <JourneyProvider>
            <Toaster />
            <PerformanceMonitor enabled={true} />
            <AppLayout>
              <Router />
            </AppLayout>
          </JourneyProvider>
        </AchievementProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
